import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { ConfigProvider } from 'antd';
import type { ThemeConfig } from 'antd';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

import light from './light.json';
import dark from './dark.json';
import type { AppTheme, ThemeTokens } from './types';

type ThemeMode = 'light' | 'dark' | 'system';

interface AppThemeContextValue {
  mode: ThemeMode;
  resolvedMode: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
}

const AppThemeContext = createContext<AppThemeContextValue>({
  mode: 'system',
  resolvedMode: 'light',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setMode: () => {},
});

const tokenSets: Record<'light' | 'dark', ThemeTokens> = { light, dark };
const STORAGE_KEY = 'rs-theme-mode';

function isValidMode(value: string | null): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

function usePersistedMode(): [ThemeMode, (mode: ThemeMode) => void] {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isValidMode(stored) ? stored : 'light';
  });

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
  }, []);

  return [mode, setMode];
}

function useSystemPreference(): 'light' | 'dark' {
  const [preference, setPreference] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setPreference(e.matches ? 'dark' : 'light');
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    // Fallback for older browsers / jsdom
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  return preference;
}

function buildAntTheme(antTokens: Omit<ThemeTokens, 'next'>): ThemeConfig {
  return {
    token: {
      ...antTokens,
      fontFamily: `'Inter', 'Roboto', 'Noto Sans', 'Open Sans', 'Lato', 'Source Sans Pro', sans-serif`,
    },
    components: {
      Button: {
        borderRadius: antTokens.borderRadiusXXL,
        borderRadiusLG: antTokens.borderRadius3XL,
        borderRadiusSM: antTokens.borderRadiusXXL,
        fontSizeLG: antTokens.fontSize,
      },
      Table: {
        borderRadius: 0,
        borderRadiusLG: 0,
      },
      Segmented: {
        colorBgLayout: antTokens.colorFillTertiary,
        itemSelectedBg: antTokens.segmentedBgColorSelected,
      },
      Modal: {
        borderRadiusLG: antTokens.borderRadiusXL,
      },
    },
  };
}

interface AppThemeProviderProps {
  children: ReactNode;
}

export function AppThemeProvider({ children }: AppThemeProviderProps) {
  const [mode, setMode] = usePersistedMode();
  const systemPref = useSystemPreference();
  const resolvedMode = mode === 'system' ? systemPref : mode;

  const tokens = tokenSets[resolvedMode];
  const { next: _next, ...antTokens } = tokens;

  const styledTheme: AppTheme = useMemo(() => ({ token: tokens }), [tokens]);

  const antThemeConfig = useMemo(() => buildAntTheme(antTokens), [tokens]);

  const contextValue = useMemo(
    () => ({ mode, resolvedMode, setMode }),
    [mode, resolvedMode, setMode],
  );

  return (
    <AppThemeContext.Provider value={contextValue}>
      <ConfigProvider theme={antThemeConfig}>
        <StyledThemeProvider theme={styledTheme}>{children}</StyledThemeProvider>
      </ConfigProvider>
    </AppThemeContext.Provider>
  );
}

// ts-prune-ignore-next
export const useAppTheme = () => useContext(AppThemeContext);

// ts-prune-ignore-next — re-exported for buildAntTheme tests
export { buildAntTheme };
